<script lang="ts">
  import { geoPermission, type GeoPermission } from '../../geolocation'
  import { isNativeShell } from '../../native'
  import { here } from '../../state/here.svelte'
  import { places, setOpenTo } from '../../state/places.svelte'

  /**
   * "Open to": My location, or a saved place (Phase 9 plan §4.4, §6.5), as
   * round 2 draws it: two choices, the second with the place under it. Read
   * once, at boot. Under it, what the browser will do about location, because
   * a blocked permission otherwise opens the app somewhere else with nothing
   * on screen to say why — Chromium turns three dismissed prompts into a week
   * of `denied` by itself.
   */

  // The boot skips the question when it opens to a saved place, so ask here.
  $effect(() => {
    void geoPermission().then((state) => (here.permission = state))
  })

  const PERMISSION_TEXT: Record<GeoPermission, string> = {
    granted: 'Location allowed.',
    denied: 'Location is blocked for this site; the app opens to the last place you viewed.',
    unsupported: "This browser can't report location.",
    insecure: 'Location needs a secure (https) connection.',
    prompt: 'The browser asks for location when the app opens.',
    unknown: 'The browser asks for location when the app opens.',
  }

  /** In the iOS app the permission is the app's, switched in iOS Settings. */
  const APP_TEXT: Partial<Record<GeoPermission, string>> = {
    denied: 'Location is off for Fusion WX in iOS Settings; the app opens to the last place you viewed.',
    prompt: 'iOS asks for location when the app opens.',
    unknown: 'iOS asks for location when the app opens.',
  }
  let permissionText = $derived(
    (isNativeShell() ? APP_TEXT[here.permission] : undefined) ?? PERMISSION_TEXT[here.permission]
  )

  let saved = $derived(places.openTo !== 'here' && places.list.some((p) => p.id === places.openTo))

  /** The place the second choice opens to: the chosen one, or the first. */
  let chosen = $derived(saved ? places.openTo : (places.list[0]?.id ?? ''))
</script>

<div class="choices" role="radiogroup" aria-label="Open to">
  <label class="choice touch-target">
    <input
      class="wx-box"
      type="radio"
      name="open-to"
      value="here"
      checked={!saved}
      onchange={() => setOpenTo('here')}
    />
    <span class="text">
      <span class="name">My location</span>
      <span class="sub">Where this device is when you open the app</span>
    </span>
  </label>
  <div class="choice place">
    <input
      id="open-to-place"
      class="wx-box"
      type="radio"
      name="open-to"
      value="place"
      checked={saved}
      disabled={places.list.length === 0}
      onchange={() => chosen && setOpenTo(chosen)}
    />
    <span class="text">
      <label class="name" for="open-to-place">A saved place</label>
      {#if places.list.length === 0}
        <span class="sub">Save a place with the star beside its name first.</span>
      {:else}
        <select
          class="wx-field touch-target"
          aria-label="Saved place to open to"
          value={chosen}
          onchange={(e) => setOpenTo(e.currentTarget.value)}
        >
          {#each places.list as place (place.id)}
            <option value={place.id}>{place.name}</option>
          {/each}
        </select>
      {/if}
    </span>
  </div>
</div>
<p class="state">{permissionText}</p>

<style>
  .choices {
    display: flex;
    flex-direction: column;
  }

  .choice {
    display: grid;
    grid-template-columns: 18px minmax(0, 1fr);
    gap: 12px;
    padding: 10px 0;
    cursor: pointer;
  }

  .choice + .choice {
    border-top: 1px solid var(--color-divider);
  }

  .choice :global(.wx-box) {
    width: 18px;
    height: 18px;
    margin-top: 1px;
  }

  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .place .text {
    gap: 8px;
  }

  .name {
    font-size: 13px;
    font-weight: 800;
    cursor: pointer;
  }

  .sub {
    font-size: 12px;
    color: var(--color-muted);
  }

  select {
    width: 100%;
    /* 16px stops iOS Safari zooming the viewport when the field is focused. */
    font-size: max(16px, var(--btn-fs, 13px));
    font-weight: 600;
  }

  @media (pointer: fine) {
    select {
      font-size: 13px;
    }
  }

  .state {
    margin: 4px 0 0;
    font-size: 12px;
    color: var(--color-muted);
  }
</style>
